import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { db, auth } from "./firebase";
import { useSchool } from "./schoolContext";
import { useToast } from "./components";
import { registerNewSchool, isValidSlug, suggestSlug, generatePassword, isSlugAvailable, updateSchool, toggleSchoolActive, softDeleteSchool, restoreSchool, sendPasswordResetForAdmin } from "./superAdminActions";
import { loadGlobalMetrics } from "./superAdminMetrics";

// ════════════════════════════════════════════════════════════════
// Super-Admin Panel — accessible at /admin
//
// Only users with role: "super_admin" in userRoles/{uid} can access.
// From here you can:
//   - List all schools (active, inactive, by plan)
//   - Create new schools (delegated to ChildPanel — future entrega)
//   - View school details and metrics
//   - Suspend / reactivate / change plan of schools
// ════════════════════════════════════════════════════════════════

export default function SuperAdminPanel() {
  const { isSuperAdmin, loading: contextLoading } = useSchool();

  // While context resolves
  if (contextLoading) {
    return <div style={fullPageStyle}><div style={{color:"#94A3B8",fontFamily:"'DM Sans'"}}>Cargando...</div></div>;
  }

  // Not logged in or not a super-admin → show login
  if (!isSuperAdmin) {
    return <SuperAdminLogin/>;
  }

  // Logged in as super-admin → show panel
  return <PanelContent/>;
}

// ─── LOGIN SCREEN ───
export function SuperAdminLogin() {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setErr(""); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pass);
      // SchoolContext will pick up the new auth state and re-render
      // If the user is not a super-admin, the parent component will not show the panel.
    } catch (e) {
      if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password" || e.code === "auth/user-not-found") {
        setErr("Correo o contraseña incorrectos");
      } else {
        setErr(e.message);
      }
    }
    setLoading(false);
  };

  return (
    <div style={fullPageStyle}>
      <div style={{background:"#1E293B",borderRadius:24,padding:"36px 32px",width:"100%",maxWidth:400,boxShadow:"0 20px 60px rgba(0,0,0,.3)",animation:"slideUp .5s ease-out"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:56,marginBottom:8}}>👑</div>
          <h1 style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:24,color:"white"}}>Super Admin</h1>
          <p style={{fontSize:14,color:"#94A3B8"}}>Panel de gestión SaaS</p>
        </div>
        <div style={{marginBottom:14}}>
          <label style={{fontSize:12,color:"#94A3B8",fontWeight:600,display:"block",marginBottom:4}}>Correo</label>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="superadmin@ejemplo.com" style={inputStyle}/>
        </div>
        <div style={{marginBottom:20}}>
          <label style={{fontSize:12,color:"#94A3B8",fontWeight:600,display:"block",marginBottom:4}}>Contraseña</label>
          <input type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••" onKeyDown={e=>e.key==="Enter"&&email.trim()&&pass.length>=6&&handleLogin()} style={inputStyle}/>
        </div>
        {err&&<div style={{background:"rgba(239,68,68,.1)",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#EF4444"}}>{err}</div>}
        <button disabled={!email.trim()||pass.length<6||loading} onClick={handleLogin} style={{width:"100%",padding:16,fontSize:17,fontFamily:"'Baloo 2',cursive",fontWeight:700,color:"white",background:email.trim()&&pass.length>=6&&!loading?"linear-gradient(135deg,#8B5CF6,#7C3AED)":"#334155",border:"none",borderRadius:14,cursor:email.trim()&&pass.length>=6?"pointer":"not-allowed"}}>{loading?"⏳...":"Entrar"}</button>
        <div style={{marginTop:20,padding:"10px 14px",background:"rgba(139,92,246,.08)",border:"1px solid rgba(139,92,246,.2)",borderRadius:10,fontSize:12,color:"#A78BFA",lineHeight:1.5}}>
          ⚠️ Acceso restringido. Solo cuentas con rol <strong>super_admin</strong> en userRoles/.
        </div>
      </div>
    </div>
  );
}

// ─── PANEL CONTENT (logged in) ───
function PanelContent() {
  const [tab, setTab] = useState("schools"); // schools | metrics (future)
  const handleLogout = async () => { try { await signOut(auth); } catch {} };

  return (
    <div style={{minHeight:"100vh",background:"#0F172A",fontFamily:"'DM Sans',sans-serif",color:"#E2E8F0"}}>
      {/* Header */}
      <header style={{background:"#1E293B",borderBottom:"1px solid rgba(255,255,255,.06)",padding:"16px 28px",position:"sticky",top:0,zIndex:100}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:44,height:44,borderRadius:12,background:"linear-gradient(135deg,#8B5CF6,#7C3AED)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>👑</div>
            <div>
              <div style={{fontFamily:"'Baloo 2',cursive",fontWeight:700,fontSize:22,color:"white"}}>Super Admin</div>
              <div style={{fontSize:13,color:"#94A3B8"}}>La Tiendita CR — Gestión SaaS</div>
            </div>
          </div>
          <button onClick={handleLogout} style={{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,.1)",background:"transparent",color:"#94A3B8",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cerrar sesión</button>
        </div>
      </header>

      {/* Tabs */}
      <nav style={{maxWidth:1200,margin:"0 auto",padding:"16px 28px 0"}}>
        <div style={{display:"flex",gap:4,background:"#1E293B",borderRadius:16,padding:4,maxWidth:400}}>
          <TabButton id="schools" active={tab} onClick={setTab} label="🏫 Escuelas"/>
          <TabButton id="metrics" active={tab} onClick={setTab} label="📊 Métricas"/>
        </div>
      </nav>

      {/* Content */}
      <main style={{maxWidth:1200,margin:"0 auto",padding:"24px 28px"}}>
        {tab==="schools" && <SchoolsList/>}
        {tab==="metrics" && <MetricsDashboard/>}
      </main>
    </div>
  );
}

function TabButton({ id, active, onClick, label }) {
  const isActive = active === id;
  return (
    <button onClick={()=>onClick(id)} style={{flex:1,padding:"12px 20px",borderRadius:14,border:"none",background:isActive?"#0F172A":"transparent",color:isActive?"white":"#64748B",fontWeight:600,fontSize:14,cursor:"pointer",fontFamily:"'DM Sans'"}}>{label}</button>
  );
}

// ─── SCHOOLS LIST ───
function SchoolsList() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | inactive
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [createdResult, setCreatedResult] = useState(null); // { schoolId, slug, adminEmail, adminPassword } after success
  const [selectedSchool, setSelectedSchool] = useState(null); // school object to show details for

  useEffect(() => {
    // Use plain collection (no orderBy) so docs without `createdAt` still appear.
    // Firestore's orderBy silently excludes docs missing the ordering field.
    const unsub = onSnapshot(collection(db, "schools"),
      (snap) => {
        const docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        // Sort client-side: newest first; docs without createdAt go last.
        docs.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt.localeCompare(a.createdAt);
        });
        setSchools(docs);
        setLoading(false);
      },
      (err) => { console.warn("schools listener error:", err.code); setLoading(false); }
    );
    return unsub;
  }, []);

  if (loading) return <div style={{textAlign:"center",padding:60,color:"#64748B"}}>Cargando escuelas...</div>;

  const filtered = schools.filter(s => {
    // "deleted" filter shows only soft-deleted schools; all others hide them
    if (filter === "deleted") {
      if (!s.deleted) return false;
    } else {
      if (s.deleted) return false;
    }
    if (filter === "active" && s.active === false) return false;
    if (filter === "inactive" && (s.active !== false || s.deleted)) return false;
    const term = search.trim().toLowerCase();
    if (term && !(s.name||"").toLowerCase().includes(term) && !(s.slug||"").toLowerCase().includes(term)) return false;
    return true;
  });

  // Stats exclude deleted schools
  const active = schools.filter(s => !s.deleted);
  const totalActive = active.filter(s => s.active !== false).length;
  const totalInactive = active.filter(s => s.active === false).length;
  const totalDeleted = schools.filter(s => s.deleted).length;

  return (
    <div>
      {/* Stats cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))",gap:14,marginBottom:24}}>
        <StatCard label="Total escuelas" value={active.length} icon="🏫" color="#8B5CF6"/>
        <StatCard label="Activas" value={totalActive} icon="✅" color="#16A34A"/>
        <StatCard label="Inactivas" value={totalInactive} icon="⏸️" color="#F59E0B"/>
        {totalDeleted > 0 && <StatCard label="Eliminadas" value={totalDeleted} icon="🗑️" color="#EF4444"/>}
      </div>

      {/* Toolbar */}
      <div style={{background:"#1E293B",borderRadius:16,padding:"14px 18px",marginBottom:16,display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Buscar por nombre o código..."
          style={{flex:1,minWidth:220,padding:"10px 14px",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:10,color:"white",fontSize:14}}
        />
        <div style={{display:"flex",gap:4,background:"rgba(0,0,0,.2)",borderRadius:10,padding:3}}>
          {[{id:"all",l:"Todas"},{id:"active",l:"Activas"},{id:"inactive",l:"Inactivas"},{id:"deleted",l:"🗑️"}].map(f =>
            <button key={f.id} onClick={()=>setFilter(f.id)} title={f.id==="deleted"?"Eliminadas":f.l} style={{padding:"7px 14px",borderRadius:8,border:"none",background:filter===f.id?"#0F172A":"transparent",color:filter===f.id?"white":"#94A3B8",fontWeight:600,fontSize:13,cursor:"pointer"}}>{f.l}</button>
          )}
        </div>
        <button onClick={()=>setShowWizard(true)} style={{padding:"10px 18px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#8B5CF6,#7C3AED)",color:"white",fontWeight:700,fontSize:14,cursor:"pointer",boxShadow:"0 4px 12px rgba(139,92,246,.3)"}}>+ Crear escuela</button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 20px",background:"#1E293B",borderRadius:16,color:"#64748B"}}>
          {schools.length === 0 ? "Aún no hay escuelas registradas." : "No hay escuelas que coincidan con los filtros."}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map(s => <SchoolRow key={s.id} school={s} onOpenDetail={()=>setSelectedSchool(s)}/>)}
        </div>
      )}

      {/* Wizard modal */}
      {showWizard && !createdResult && (
        <CreateSchoolWizard
          onClose={()=>setShowWizard(false)}
          onSuccess={(result)=>{ setCreatedResult(result); }}
        />
      )}

      {/* Success screen */}
      {createdResult && (
        <SuccessScreen
          result={createdResult}
          onClose={()=>{ setCreatedResult(null); setShowWizard(false); }}
        />
      )}

      {/* School detail modal */}
      {selectedSchool && (
        <SchoolDetailModal
          school={selectedSchool}
          onClose={()=>setSelectedSchool(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div style={{background:"#1E293B",borderRadius:16,padding:"18px 20px",border:`1px solid ${color}30`}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
        <div style={{width:36,height:36,borderRadius:10,background:`${color}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{icon}</div>
        <div style={{fontSize:13,color:"#94A3B8",fontWeight:600}}>{label}</div>
      </div>
      <div style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:32,color:"white"}}>{value}</div>
    </div>
  );
}

function SchoolRow({ school, onOpenDetail }) {
  const isActive = school.active !== false;
  const planColors = { free:"#94A3B8", basic:"#3B82F6", premium:"#F59E0B" };
  const planColor = planColors[school.plan] || "#94A3B8";
  const slug = school.slug || school.id;
  const url = `/school/${slug}/vendedor`;

  return (
    <div style={{background:"#1E293B",borderRadius:14,padding:"16px 20px",border:"1px solid rgba(255,255,255,.04)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:16,flexWrap:"wrap"}}>
      <div style={{flex:"1 1 240px",minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
          <div style={{fontWeight:700,fontSize:16,color:"white"}}>{school.name || "(sin nombre)"}</div>
          <span style={{fontSize:11,color:planColor,background:`${planColor}15`,padding:"2px 8px",borderRadius:6,fontWeight:700,textTransform:"uppercase"}}>{school.plan || "free"}</span>
          {school.deleted && <span style={{fontSize:11,color:"#EF4444",background:"rgba(239,68,68,.15)",padding:"2px 8px",borderRadius:6,fontWeight:700}}>🗑️ ELIMINADA</span>}
          {!isActive && !school.deleted && <span style={{fontSize:11,color:"#EF4444",background:"rgba(239,68,68,.15)",padding:"2px 8px",borderRadius:6,fontWeight:700}}>INACTIVA</span>}
        </div>
        <div style={{fontSize:13,color:"#64748B",fontFamily:"'DM Sans'"}}>
          /{slug}
          {school.contactEmail && <span> · ✉️ {school.contactEmail}</span>}
          {school.createdAt && <span> · 📅 {new Date(school.createdAt).toLocaleDateString("es-CR",{day:"2-digit",month:"short",year:"numeric"})}</span>}
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <a href={url} target="_blank" rel="noreferrer" style={{padding:"8px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,.1)",background:"transparent",color:"#94A3B8",fontSize:13,fontWeight:600,cursor:"pointer",textDecoration:"none"}}>↗ Abrir</a>
        <button onClick={onOpenDetail} style={{padding:"8px 14px",borderRadius:10,border:"1px solid rgba(139,92,246,.3)",background:"rgba(139,92,246,.1)",color:"#A78BFA",fontSize:13,fontWeight:600,cursor:"pointer"}}>Detalles</button>
      </div>
    </div>
  );
}

function MetricsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const m = await loadGlobalMetrics();
      setData(m);
    } catch (e) {
      console.error("metrics load error:", e);
      setError(e.message || "Error al cargar métricas");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div style={{textAlign:"center",padding:"80px 20px",background:"#1E293B",borderRadius:16,color:"#64748B"}}>⏳ Cargando métricas globales...</div>;
  }
  if (error && !data) {
    return <div style={{textAlign:"center",padding:"40px 20px",background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.2)",borderRadius:16,color:"#FCA5A5"}}>❌ {error}<br/><button onClick={load} style={{...btnSecondary,marginTop:14}}>Reintentar</button></div>;
  }
  if (!data) return null;

  const planTotal = data.planBuckets.free + data.planBuckets.basic + data.planBuckets.premium || 1;
  const planPct = (n) => Math.round((n / planTotal) * 100);

  return (
    <div>
      {/* Refresh bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:13,color:"#64748B"}}>
          📅 Última actualización: {new Date(data.loadedAt).toLocaleTimeString("es-CR",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}
        </div>
        <button onClick={load} disabled={loading} style={{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(139,92,246,.3)",background:"rgba(139,92,246,.1)",color:"#A78BFA",fontSize:13,fontWeight:600,cursor:loading?"wait":"pointer"}}>{loading?"⏳ Actualizando":"🔄 Refrescar"}</button>
      </div>

      {/* Schools stats */}
      <div style={{marginBottom:10,fontSize:13,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Escuelas</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:12,marginBottom:28}}>
        <StatCard label="Total activas" value={data.schoolsActive} icon="🏫" color="#8B5CF6"/>
        <StatCard label="Suspendidas" value={data.schoolsSuspended} icon="⏸️" color="#F59E0B"/>
      </div>

      {/* Users stats */}
      <div style={{marginBottom:10,fontSize:13,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Usuarios</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:12,marginBottom:28}}>
        <StatCard label="Vendedores" value={data.totals.vendors} icon="🧑‍🍳" color="#3B82F6"/>
        <StatCard label="Padres" value={data.totals.parents} icon="👨‍👩‍👧" color="#EC4899"/>
        <StatCard label="Estudiantes" value={data.totals.students} icon="🎒" color="#10B981"/>
      </div>

      {/* Orders/invoices stats */}
      <div style={{marginBottom:10,fontSize:13,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>Actividad</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))",gap:12,marginBottom:28}}>
        <StatCard label="Pedidos totales" value={data.totals.orders} icon="🛒" color="#F59E0B"/>
        <StatCard label="Pedidos del mes" value={data.totals.ordersThisMonth} icon="📆" color="#F97316"/>
        <StatCard label="Facturas pagadas (mes)" value={data.totals.paidThisMonth} icon="💸" color="#16A34A"/>
      </div>

      {/* Plan distribution */}
      <div style={{background:"#1E293B",borderRadius:16,padding:"20px 22px",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <span style={{fontSize:18}}>📦</span>
          <h3 style={{fontFamily:"'Baloo 2',cursive",fontWeight:700,fontSize:16,color:"white"}}>Distribución por plan</h3>
        </div>
        <PlanBar label="Free" value={data.planBuckets.free} pct={planPct(data.planBuckets.free)} color="#94A3B8"/>
        <PlanBar label="Basic" value={data.planBuckets.basic} pct={planPct(data.planBuckets.basic)} color="#3B82F6"/>
        <PlanBar label="Premium" value={data.planBuckets.premium} pct={planPct(data.planBuckets.premium)} color="#F59E0B"/>
      </div>

      {/* Top schools */}
      <div style={{background:"#1E293B",borderRadius:16,padding:"20px 22px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <span style={{fontSize:18}}>🏆</span>
          <h3 style={{fontFamily:"'Baloo 2',cursive",fontWeight:700,fontSize:16,color:"white"}}>Top 5 escuelas por pedidos</h3>
        </div>
        {data.topByOrders.length === 0 ? (
          <div style={{color:"#64748B",fontSize:14,textAlign:"center",padding:20}}>Aún no hay pedidos.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {data.topByOrders.map((s, i) => (
              <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",background:"rgba(255,255,255,.03)",borderRadius:10}}>
                <div style={{width:28,height:28,borderRadius:8,background:i===0?"rgba(245,158,11,.2)":"rgba(255,255,255,.05)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,color:i===0?"#F59E0B":"#94A3B8",fontSize:13}}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,color:"white",fontWeight:600}}>{s.name || "(sin nombre)"}</div>
                  <div style={{fontSize:12,color:"#64748B"}}>/{s.slug}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:20,color:"white"}}>{s.orders}</div>
                  <div style={{fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase"}}>pedidos</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlanBar({ label, value, pct, color }) {
  return (
    <div style={{marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,fontSize:13}}>
        <span style={{color,fontWeight:700}}>{label}</span>
        <span style={{color:"#94A3B8"}}>{value} <span style={{fontSize:11,color:"#64748B"}}>({pct}%)</span></span>
      </div>
      <div style={{height:8,borderRadius:4,background:"rgba(255,255,255,.04)",overflow:"hidden"}}>
        <div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:4,transition:"width .4s ease"}}/>
      </div>
    </div>
  );
}


// ─── CREATE SCHOOL WIZARD ───
export function CreateSchoolWizard({ onClose, onSuccess }) {
  const toast = useToast();
  const [step, setStep] = useState(1); // 1 = school data, 2 = admin data
  // School data
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false); // user manually edited slug?
  const [contactEmail, setContactEmail] = useState("");
  const [plan, setPlan] = useState("free");
  const [slugStatus, setSlugStatus] = useState(null); // null | "checking" | "available" | "taken" | "invalid"
  // Admin data
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState(() => generatePassword());
  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Auto-generate slug from name unless user has manually edited it
  useEffect(() => {
    if (!slugManual) {
      setSlug(suggestSlug(name));
      setSlugStatus(null);
    }
  }, [name, slugManual]);

  // Check slug availability when user blurs the slug field
  const checkSlug = async () => {
    if (!slug) { setSlugStatus(null); return; }
    if (!isValidSlug(slug)) { setSlugStatus("invalid"); return; }
    setSlugStatus("checking");
    try {
      const ok = await isSlugAvailable(slug);
      setSlugStatus(ok ? "available" : "taken");
    } catch {
      setSlugStatus(null);
    }
  };

  const canProceedStep1 = name.trim() && isValidSlug(slug) && contactEmail.trim() && slugStatus !== "taken";
  const canSubmit = adminName.trim() && adminEmail.trim() && adminPassword.length >= 6;

  const handleSubmit = async () => {
    setError(""); setSubmitting(true);
    try {
      const result = await registerNewSchool(
        { name: name.trim(), slug: slug.trim().toLowerCase(), contactEmail: contactEmail.trim(), plan },
        { name: adminName.trim(), email: adminEmail.trim(), password: adminPassword }
      );
      onSuccess({
        schoolId: result.schoolId,
        slug: result.slug,
        schoolName: name.trim(),
        adminEmail: adminEmail.trim(),
        adminPassword,
      });
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  };

  const slugFeedback = () => {
    if (!slug) return null;
    if (slugStatus === "checking") return <span style={{fontSize:12,color:"#94A3B8"}}>⏳ Verificando...</span>;
    if (slugStatus === "available") return <span style={{fontSize:12,color:"#16A34A",fontWeight:600}}>✓ Disponible</span>;
    if (slugStatus === "taken") return <span style={{fontSize:12,color:"#EF4444",fontWeight:600}}>✕ Ya está en uso</span>;
    if (slugStatus === "invalid") return <span style={{fontSize:12,color:"#F59E0B",fontWeight:600}}>⚠ Solo letras minúsculas, números, guiones (2-40)</span>;
    return null;
  };

  return (
    <div style={modalOverlay} onClick={(e)=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={modalContent}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div>
            <h2 style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:22,color:"white",marginBottom:4}}>+ Crear nueva escuela</h2>
            <div style={{fontSize:13,color:"#64748B"}}>Paso {step} de 2 · {step===1?"Datos de la escuela":"Administrador inicial"}</div>
          </div>
          <button onClick={onClose} style={{padding:"6px 10px",borderRadius:8,border:"none",background:"transparent",color:"#64748B",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{display:"flex",gap:6,marginBottom:24}}>
          <div style={{flex:1,height:4,borderRadius:2,background:step>=1?"#8B5CF6":"#334155"}}/>
          <div style={{flex:1,height:4,borderRadius:2,background:step>=2?"#8B5CF6":"#334155"}}/>
        </div>

        {step === 1 && (
          <div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Nombre de la escuela *</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Colegio San Pedro" style={inputStyle}/>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Código (slug) — usado en la URL *</label>
              <input
                value={slug}
                onChange={e=>{setSlugManual(true);setSlug(e.target.value.toLowerCase());setSlugStatus(null)}}
                onBlur={checkSlug}
                placeholder="colegio-san-pedro"
                style={inputStyle}
              />
              <div style={{marginTop:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <div style={{fontSize:12,color:"#64748B"}}>URL: <code style={{color:"#A78BFA"}}>/school/{slug || "..."}</code></div>
                {slugFeedback()}
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Email de contacto institucional *</label>
              <input type="email" value={contactEmail} onChange={e=>setContactEmail(e.target.value)} placeholder="contacto@colegio.cr" style={inputStyle}/>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Plan</label>
              <div style={{display:"flex",gap:8}}>
                {[{id:"free",l:"Free",c:"#94A3B8"},{id:"basic",l:"Basic",c:"#3B82F6"},{id:"premium",l:"Premium",c:"#F59E0B"}].map(p =>
                  <button key={p.id} onClick={()=>setPlan(p.id)} style={{flex:1,padding:"10px 12px",borderRadius:10,border:`2px solid ${plan===p.id?p.c:"rgba(255,255,255,.08)"}`,background:plan===p.id?`${p.c}15`:"transparent",color:plan===p.id?p.c:"#94A3B8",fontWeight:700,fontSize:14,cursor:"pointer"}}>{p.l}</button>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:24}}>
              <button onClick={onClose} style={btnSecondary}>Cancelar</button>
              <button disabled={!canProceedStep1} onClick={()=>setStep(2)} style={canProceedStep1?btnPrimary:btnDisabled}>Siguiente →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Nombre del administrador *</label>
              <input value={adminName} onChange={e=>setAdminName(e.target.value)} placeholder="María González" style={inputStyle}/>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Email del administrador *</label>
              <input type="email" value={adminEmail} onChange={e=>setAdminEmail(e.target.value)} placeholder="admin@colegio.cr" style={inputStyle}/>
              <div style={{fontSize:12,color:"#64748B",marginTop:6}}>Será su login para entrar al sistema.</div>
            </div>
            <div style={fieldGroup}>
              <label style={fieldLabel}>Contraseña temporal *</label>
              <div style={{display:"flex",gap:8}}>
                <input value={adminPassword} onChange={e=>setAdminPassword(e.target.value)} style={{...inputStyle,fontFamily:"monospace",letterSpacing:1}}/>
                <button onClick={()=>setAdminPassword(generatePassword())} title="Regenerar" style={{padding:"0 14px",borderRadius:10,border:"1px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.04)",color:"#94A3B8",cursor:"pointer",fontSize:18}}>🔄</button>
              </div>
              <div style={{fontSize:12,color:"#64748B",marginTop:6}}>Auto-generada. Le vas a entregar estas credenciales al cliente.</div>
            </div>
            {error && <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.3)",borderRadius:10,padding:"12px 14px",fontSize:13,color:"#FCA5A5",marginTop:16,marginBottom:8,lineHeight:1.5}}>{error}</div>}
            <div style={{display:"flex",gap:10,marginTop:24}}>
              <button onClick={()=>setStep(1)} disabled={submitting} style={btnSecondary}>← Atrás</button>
              <button disabled={!canSubmit||submitting} onClick={handleSubmit} style={canSubmit&&!submitting?btnPrimary:btnDisabled}>{submitting?"⏳ Creando...":"Crear escuela"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SUCCESS SCREEN ───
function SuccessScreen({ result, onClose }) {
  const toast = useToast();
  const url = `${window.location.origin}/school/${result.slug}/vendedor`;
  const credentialsText = `🏫 ${result.schoolName}\n\n🔗 URL de acceso:\n${url}\n\n👤 Email del admin: ${result.adminEmail}\n🔑 Contraseña temporal: ${result.adminPassword}\n\n⚠️ Cambie la contraseña al primer login.`;

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(credentialsText);
      toast.success("Credenciales copiadas");
    } catch {
      toast.error("No se pudieron copiar. Selecciona el texto manualmente.");
    }
  };

  return (
    <div style={modalOverlay} onClick={(e)=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={modalContent}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:56,marginBottom:8}}>🎉</div>
          <h2 style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:24,color:"white",marginBottom:6}}>¡Escuela creada!</h2>
          <div style={{fontSize:14,color:"#94A3B8"}}>{result.schoolName} ya está lista para operar.</div>
        </div>

        <div style={{background:"rgba(139,92,246,.08)",border:"1px solid rgba(139,92,246,.25)",borderRadius:14,padding:18,marginBottom:16}}>
          <div style={{fontSize:12,color:"#94A3B8",fontWeight:600,marginBottom:4}}>🔗 URL de acceso</div>
          <div style={{fontSize:14,color:"#A78BFA",fontWeight:600,wordBreak:"break-all",marginBottom:14}}>{url}</div>
          <div style={{fontSize:12,color:"#94A3B8",fontWeight:600,marginBottom:4}}>👤 Email del admin</div>
          <div style={{fontSize:15,color:"white",fontWeight:600,marginBottom:14,fontFamily:"monospace"}}>{result.adminEmail}</div>
          <div style={{fontSize:12,color:"#94A3B8",fontWeight:600,marginBottom:4}}>🔑 Contraseña temporal</div>
          <div style={{fontSize:18,color:"white",fontWeight:700,fontFamily:"monospace",letterSpacing:1,padding:"8px 12px",background:"rgba(0,0,0,.3)",borderRadius:8,display:"inline-block"}}>{result.adminPassword}</div>
        </div>

        <div style={{background:"rgba(245,158,11,.08)",border:"1px solid rgba(245,158,11,.2)",borderRadius:10,padding:"12px 14px",fontSize:13,color:"#FBBF24",marginBottom:20,lineHeight:1.5}}>
          ⚠️ <strong>Importante:</strong> Esta es la única vez que se va a mostrar esta contraseña. Copiala y entregala al administrador. Recomendá que la cambie al primer login.
        </div>

        <div style={{display:"flex",gap:10}}>
          <button onClick={copyCredentials} style={btnSecondary}>📋 Copiar credenciales</button>
          <button onClick={onClose} style={btnPrimary}>Listo</button>
        </div>
      </div>
    </div>
  );
}

const fullPageStyle = {
  minHeight:"100vh",
  background:"linear-gradient(160deg,#0F172A 0%,#1E293B 50%,#0F172A 100%)",
  fontFamily:"'DM Sans',sans-serif",
  display:"flex",
  alignItems:"center",
  justifyContent:"center",
  padding:24
};

const inputStyle = {
  width:"100%",
  padding:"14px 16px",
  background:"rgba(255,255,255,.06)",
  border:"1px solid rgba(255,255,255,.12)",
  borderRadius:12,
  color:"white",
  fontSize:15,
  fontWeight:500,
  fontFamily:"'DM Sans'"
};

// ─── WIZARD STYLES ───
// ─── SCHOOL DETAIL MODAL ───
export function SchoolDetailModal({ school, onClose }) {
  const toast = useToast();

  // Editable fields
  const [name, setName] = useState(school.name || "");
  const [plan, setPlan] = useState(school.plan || "free");
  const [contactEmail, setContactEmail] = useState(school.contactEmail || "");
  const [currency, setCurrency] = useState(school.config?.currency || "CRC");
  const [locale, setLocale] = useState(school.config?.locale || "es-CR");
  const [saving, setSaving] = useState(false);

  // Counters (live from subcollections)
  const [counts, setCounts] = useState({ vendors: null, parents: null, students: null, orders: null });

  // Admins list
  const [admins, setAdmins] = useState([]);

  // Action state
  const [busy, setBusy] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Subscribe to counters + admins list
  useEffect(() => {
    if (!school?.id) return;
    const sid = school.id;
    const unsubVendors = onSnapshot(collection(db, "schools", sid, "vendors"),
      (snap) => {
        setCounts(c => ({ ...c, vendors: snap.size }));
        // Admins = vendors with isAdmin === true
        const a = snap.docs.map(d => ({ ...d.data(), id: d.id })).filter(v => v.isAdmin === true);
        setAdmins(a);
      },
      (err) => { console.warn("detail/vendors error:", err.code); }
    );
    const unsubParents = onSnapshot(collection(db, "schools", sid, "parents"),
      (snap) => setCounts(c => ({ ...c, parents: snap.size })),
      (err) => { console.warn("detail/parents error:", err.code); }
    );
    const unsubStudents = onSnapshot(collection(db, "schools", sid, "students"),
      (snap) => setCounts(c => ({ ...c, students: snap.size })),
      (err) => { console.warn("detail/students error:", err.code); }
    );
    const unsubOrders = onSnapshot(collection(db, "schools", sid, "orders"),
      (snap) => setCounts(c => ({ ...c, orders: snap.size })),
      (err) => { console.warn("detail/orders error:", err.code); }
    );
    return () => { unsubVendors(); unsubParents(); unsubStudents(); unsubOrders(); };
  }, [school?.id]);

  // Track if form has changes vs current school data
  const hasChanges = name !== (school.name || "")
    || plan !== (school.plan || "free")
    || contactEmail !== (school.contactEmail || "")
    || currency !== (school.config?.currency || "CRC")
    || locale !== (school.config?.locale || "es-CR");

  const handleSave = async () => {
    if (!name.trim()) { toast.error("El nombre no puede estar vacío"); return; }
    setSaving(true);
    try {
      await updateSchool(school.id, {
        name: name.trim(),
        plan,
        contactEmail: contactEmail.trim(),
        config: { ...(school.config || {}), currency, locale },
      });
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    setBusy(true);
    try {
      const newActive = school.active === false;
      await toggleSchoolActive(school.id, newActive);
      toast.success(newActive ? "Escuela reactivada" : "Escuela suspendida");
      setConfirmSuspend(false);
    } catch (e) {
      toast.error(e.message || "Error");
    }
    setBusy(false);
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await softDeleteSchool(school.id);
      toast.success("Escuela marcada como eliminada");
      setConfirmDelete(false);
      onClose();
    } catch (e) {
      toast.error(e.message || "Error al eliminar");
    }
    setBusy(false);
  };

  const handleRestore = async () => {
    setBusy(true);
    try {
      await restoreSchool(school.id);
      toast.success("Escuela restaurada");
    } catch (e) {
      toast.error(e.message || "Error al restaurar");
    }
    setBusy(false);
  };

  const handleResetPassword = async (email) => {
    if (!email) { toast.error("El admin no tiene email registrado"); return; }
    try {
      await sendPasswordResetForAdmin(email);
      toast.success(`Email de reset enviado a ${email}`);
    } catch (e) {
      toast.error(e.message || "Error enviando reset");
    }
  };

  const isActive = school.active !== false;
  const isDeleted = !!school.deleted;
  const slug = school.slug || school.id;
  const url = `${window.location.origin}/school/${slug}/vendedor`;

  return (
    <div style={modalOverlay} onClick={(e)=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={{...modalContent, maxWidth:720}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
              <h2 style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:22,color:"white"}}>{school.name || "(sin nombre)"}</h2>
              {isDeleted && <span style={{fontSize:11,color:"#EF4444",background:"rgba(239,68,68,.15)",padding:"2px 8px",borderRadius:6,fontWeight:700}}>🗑️ ELIMINADA</span>}
              {!isActive && !isDeleted && <span style={{fontSize:11,color:"#F59E0B",background:"rgba(245,158,11,.15)",padding:"2px 8px",borderRadius:6,fontWeight:700}}>SUSPENDIDA</span>}
              {isActive && !isDeleted && <span style={{fontSize:11,color:"#16A34A",background:"rgba(22,163,74,.15)",padding:"2px 8px",borderRadius:6,fontWeight:700}}>ACTIVA</span>}
            </div>
            <div style={{fontSize:13,color:"#64748B",wordBreak:"break-all"}}>{url}</div>
          </div>
          <button onClick={onClose} style={{padding:"6px 10px",borderRadius:8,border:"none",background:"transparent",color:"#64748B",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* Section 1: counters */}
        <SectionHeader icon="📊" title="Estadísticas"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))",gap:10,marginBottom:24}}>
          <MiniStat label="Vendedores" value={counts.vendors}/>
          <MiniStat label="Padres" value={counts.parents}/>
          <MiniStat label="Estudiantes" value={counts.students}/>
          <MiniStat label="Pedidos" value={counts.orders}/>
        </div>
        {school.createdAt && <div style={{fontSize:12,color:"#64748B",marginBottom:24,marginTop:-16}}>📅 Creada el {new Date(school.createdAt).toLocaleDateString("es-CR",{day:"2-digit",month:"long",year:"numeric"})}</div>}

        {/* Section 2: editable data */}
        <SectionHeader icon="✏️" title="Datos editables"/>
        <div style={fieldGroup}>
          <label style={fieldLabel}>Nombre</label>
          <input value={name} onChange={e=>setName(e.target.value)} style={inputStyle}/>
        </div>
        <div style={fieldGroup}>
          <label style={fieldLabel}>Plan</label>
          <div style={{display:"flex",gap:8}}>
            {[{id:"free",l:"Free",c:"#94A3B8"},{id:"basic",l:"Basic",c:"#3B82F6"},{id:"premium",l:"Premium",c:"#F59E0B"}].map(p =>
              <button key={p.id} onClick={()=>setPlan(p.id)} style={{flex:1,padding:"10px 12px",borderRadius:10,border:`2px solid ${plan===p.id?p.c:"rgba(255,255,255,.08)"}`,background:plan===p.id?`${p.c}15`:"transparent",color:plan===p.id?p.c:"#94A3B8",fontWeight:700,fontSize:14,cursor:"pointer"}}>{p.l}</button>
            )}
          </div>
        </div>
        <div style={fieldGroup}>
          <label style={fieldLabel}>Email de contacto</label>
          <input type="email" value={contactEmail} onChange={e=>setContactEmail(e.target.value)} style={inputStyle}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
          <div>
            <label style={fieldLabel}>Moneda</label>
            <input value={currency} onChange={e=>setCurrency(e.target.value.toUpperCase())} placeholder="CRC" style={inputStyle}/>
          </div>
          <div>
            <label style={fieldLabel}>Locale</label>
            <input value={locale} onChange={e=>setLocale(e.target.value)} placeholder="es-CR" style={inputStyle}/>
          </div>
        </div>
        <div style={{marginBottom:28}}>
          <button disabled={!hasChanges||saving} onClick={handleSave} style={hasChanges&&!saving?{...btnPrimary,flex:"none",width:"100%"}:{...btnDisabled,flex:"none",width:"100%"}}>{saving?"⏳ Guardando...":hasChanges?"💾 Guardar cambios":"Sin cambios"}</button>
        </div>

        {/* Section 3: admins */}
        <SectionHeader icon="👥" title={`Administradores (${admins.length})`}/>
        {admins.length === 0 ? (
          <div style={{padding:"14px 16px",background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.2)",borderRadius:10,fontSize:13,color:"#FCA5A5",marginBottom:24}}>⚠️ Esta escuela no tiene ningún administrador registrado.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24}}>
            {admins.map(a => (
              <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:"rgba(255,255,255,.03)",borderRadius:10,gap:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,color:"white",fontWeight:600}}>{a.name || "(sin nombre)"}</div>
                  <div style={{fontSize:12,color:"#64748B",fontFamily:"monospace",wordBreak:"break-all"}}>{a.email || "(sin email)"}</div>
                </div>
                <button onClick={()=>handleResetPassword(a.email)} style={{padding:"8px 14px",borderRadius:8,border:"1px solid rgba(139,92,246,.3)",background:"rgba(139,92,246,.1)",color:"#A78BFA",fontSize:12,fontWeight:600,cursor:"pointer"}}>🔑 Resetear contraseña</button>
              </div>
            ))}
          </div>
        )}

        {/* Section 4: dangerous actions */}
        <SectionHeader icon="⚠️" title="Acciones peligrosas"/>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>

          {/* Suspend / Reactivate */}
          {!isDeleted && (isActive ? (
            !confirmSuspend ? (
              <button onClick={()=>setConfirmSuspend(true)} style={dangerBtn("#F59E0B")}>⏸️ Suspender escuela</button>
            ) : (
              <div style={{padding:14,background:"rgba(245,158,11,.08)",border:"1px solid rgba(245,158,11,.3)",borderRadius:10}}>
                <div style={{fontSize:13,color:"#FBBF24",marginBottom:10,lineHeight:1.5}}>⚠️ Al suspender, nadie de esta escuela podrá iniciar sesión. Los datos se conservan. ¿Continuar?</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setConfirmSuspend(false)} disabled={busy} style={{flex:1,...btnSecondary}}>Cancelar</button>
                  <button onClick={handleToggleActive} disabled={busy} style={{flex:1,...dangerBtn("#F59E0B"),fontFamily:"'Baloo 2',cursive"}}>{busy?"⏳":"Sí, suspender"}</button>
                </div>
              </div>
            )
          ) : (
            <button onClick={handleToggleActive} disabled={busy} style={dangerBtn("#16A34A")}>{busy?"⏳":"▶️ Reactivar escuela"}</button>
          ))}

          {/* Delete / Restore */}
          {!isDeleted ? (
            !confirmDelete ? (
              <button onClick={()=>setConfirmDelete(true)} style={dangerBtn("#EF4444")}>🗑️ Eliminar escuela</button>
            ) : (
              <div style={{padding:14,background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.3)",borderRadius:10}}>
                <div style={{fontSize:13,color:"#FCA5A5",marginBottom:10,lineHeight:1.5}}>🗑️ La escuela se marcará como eliminada y desaparecerá de la lista principal. Los datos se conservan y se puede restaurar más adelante. ¿Continuar?</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setConfirmDelete(false)} disabled={busy} style={{flex:1,...btnSecondary}}>Cancelar</button>
                  <button onClick={handleDelete} disabled={busy} style={{flex:1,...dangerBtn("#EF4444"),fontFamily:"'Baloo 2',cursive"}}>{busy?"⏳":"Sí, eliminar"}</button>
                </div>
              </div>
            )
          ) : (
            <button onClick={handleRestore} disabled={busy} style={dangerBtn("#16A34A")}>{busy?"⏳":"♻️ Restaurar escuela"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,marginTop:4}}>
      <span style={{fontSize:16}}>{icon}</span>
      <h3 style={{fontFamily:"'Baloo 2',cursive",fontWeight:700,fontSize:16,color:"white"}}>{title}</h3>
      <div style={{flex:1,height:1,background:"rgba(255,255,255,.06)",marginLeft:6}}/>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={{background:"rgba(255,255,255,.03)",borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
      <div style={{fontFamily:"'Baloo 2',cursive",fontWeight:800,fontSize:22,color:"white"}}>{value === null ? "…" : value}</div>
      <div style={{fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
    </div>
  );
}

function dangerBtn(color) {
  return {
    padding:"12px 18px",
    borderRadius:10,
    border:`1px solid ${color}40`,
    background:`${color}10`,
    color,
    fontWeight:600,
    fontSize:14,
    cursor:"pointer",
    fontFamily:"'DM Sans'",
    textAlign:"left"
  };
}


const modalOverlay = {
  position:"fixed",
  top:0, left:0, right:0, bottom:0,
  background:"rgba(0,0,0,.6)",
  backdropFilter:"blur(4px)",
  display:"flex",
  alignItems:"center",
  justifyContent:"center",
  padding:24,
  zIndex:1000,
  animation:"fadeIn .2s ease-out"
};

const modalContent = {
  background:"#1E293B",
  borderRadius:24,
  padding:"32px 28px",
  width:"100%",
  maxWidth:520,
  maxHeight:"90vh",
  overflowY:"auto",
  boxShadow:"0 20px 60px rgba(0,0,0,.5)",
  border:"1px solid rgba(255,255,255,.05)",
  fontFamily:"'DM Sans',sans-serif"
};

const fieldGroup = {
  marginBottom:18
};

const fieldLabel = {
  display:"block",
  fontSize:13,
  color:"#94A3B8",
  fontWeight:600,
  marginBottom:6
};

const btnPrimary = {
  flex:1,
  padding:"14px 20px",
  borderRadius:12,
  border:"none",
  background:"linear-gradient(135deg,#8B5CF6,#7C3AED)",
  color:"white",
  fontWeight:700,
  fontSize:15,
  cursor:"pointer",
  fontFamily:"'Baloo 2',cursive"
};

const btnSecondary = {
  flex:1,
  padding:"14px 20px",
  borderRadius:12,
  border:"1px solid rgba(255,255,255,.1)",
  background:"transparent",
  color:"#94A3B8",
  fontWeight:600,
  fontSize:14,
  cursor:"pointer",
  fontFamily:"'DM Sans'"
};

const btnDisabled = {
  flex:1,
  padding:"14px 20px",
  borderRadius:12,
  border:"none",
  background:"#334155",
  color:"#64748B",
  fontWeight:700,
  fontSize:15,
  cursor:"not-allowed",
  fontFamily:"'Baloo 2',cursive"
};
